// Nav registry: the menu (Containers/Nav.js) is the one source of page names,
// groups and permission gates. Every <NavItem> that survives its <ShowIf>
// registers { to, label, trail } here while mounted, so whatever reads the
// registry (command palette, active-menu highlight, PageToolbar eyebrow) sees
// exactly the pages this user may open — no second list to keep in sync.
import { createContext, useSyncExternalStore } from "react";

/** Group labels from the top-level dropdown down to the current flyout. */
export const NavTrailContext = createContext([]);

const items = new Map();
const subscribers = new Set();
let snapshot = [];
let scheduled = false;

// ~200 items mount at once; publish one snapshot per tick, not one per item.
const publish = () => {
    if (scheduled) return;
    scheduled = true;
    Promise.resolve().then(() => {
        scheduled = false;
        snapshot = [...items.values()];
        subscribers.forEach((fn) => fn());
    });
};

export function registerNavItem(item) {
    const key = `${item.trail.join(" › ")}|${item.to}|${item.label}`;
    items.set(key, item);
    publish();
    return () => {
        items.delete(key);
        publish();
    };
}

const subscribe = (fn) => {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
};
const getSnapshot = () => snapshot;

/** All menu pages the current user can open: [{ to, label, trail }] */
export function useNavItems() {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const clean = (p) => (p || "").split(/[?#]/)[0].replace(/\/+$/, "") || "/";

/**
 * The menu entry a pathname belongs to: an exact match, else the longest menu
 * path that is a whole-segment prefix of it (/salesbill/details/9 → /salesbill).
 */
export function findNavItem(pathname, list) {
    const path = clean(pathname);
    let best = null;
    let bestLen = -1;
    for (const it of list) {
        const to = clean(it.to);
        if (to === "/") continue;
        if (to === path) return it;
        if (path.startsWith(`${to}/`) && to.length > bestLen) {
            best = it;
            bestLen = to.length;
        }
    }
    return best;
}

/** Plain text of a React node (labels may carry badges/icons). */
export function nodeText(node) {
    if (node == null || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(nodeText).join("");
    if (node.props) return nodeText(node.props.children);
    return "";
}

/** Test hook */
export const __resetNavRegistryForTests = () => {
    items.clear();
    snapshot = [];
};
